/**
 * Leximi i një QELIZE të huaj — pjesa e përbashkët e çdo importi.
 *
 * Një datë e shkruar «15.8.2025», një shumë «1.234,56 €» dhe një kokë kolone
 * «Datëlindja» nuk janë probleme të stafit apo të aseteve; janë probleme të
 * Excel-it. Derisa rrinin brenda `staffImport.js`, importi i dytë duhej ose t'i
 * kopjonte (dy versione të së njëjtës rregull, që ndahen te rregullimi i parë),
 * ose të varej nga një modul që s'ka lidhje me të.
 *
 * Rregullat vetë nuk ndryshuan: `staffImport.js` i ri-eksporton, ndaj çdo import
 * ekzistues dhe çdo test i tij lexon pikërisht të njëjtat funksione.
 */

// ── Normalizimi i teksteve ───────────────────────────────────────────────────
// Diakritikat bien (NFD + heqje e shenjave): "Datëlindja" dhe "Datelindja" janë
// e njëjta kolonë, dhe klienti nuk e ka gjithmonë tastierën shqipe.
export function normKey(s) {
  return String(s ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

const pad = (n) => String(n).padStart(2, '0');
export const ymd = (y, m, d) => `${y}-${pad(m)}-${pad(d)}`;

export function safeYmd(y, m, d) {
  if (!(y >= 1900 && y <= 2200) || !(m >= 1 && m <= 12) || !(d >= 1 && d <= 31)) return null;
  // 31 shkurt duhet të bjerë, jo të rrëshqasë te 3 marsi.
  const probe = new Date(Date.UTC(y, m - 1, d));
  if (probe.getUTCMonth() + 1 !== m || probe.getUTCDate() !== d) return null;
  return ymd(y, m, d);
}

/** Dita e sotme si `YYYY-MM-DD`, e lexuar me pjesë lokale (jo UTC). */
export function todayISO(now = new Date()) {
  return ymd(now.getFullYear(), now.getMonth() + 1, now.getDate());
}

/**
 * Data nga një qelizë e panjohur → `YYYY-MM-DD`, ose `null`.
 *
 * Pranon: Date (kur `cellDates` e ka kthyer vetë), numër serial të Excel-it,
 * ISO me ose pa orë, `dd/MM/yyyy`, `dd.MM.yyyy`, `d/M/yyyy`.
 *
 * `dd/MM` kundrejt `MM/dd`: kur pjesa e parë kalon 12 është ditë pa dyshim;
 * përndryshe lexohet si DITË, sepse kjo është radha e Kosovës dhe e Evropës.
 * Kthimi `ambiguous` e thotë hapur që rreshti mund të jetë keqlexuar, dhe
 * ekrani e shënon si paralajmërim — një datë e kthyer në heshtje (03/04 →
 * 3 prill apo 4 mars?) është gabim që zbulohet vetëm muaj më vonë.
 */
export function parseDateCell(v) {
  if (v === null || v === undefined || v === '') return { value: null, ambiguous: false };

  if (v instanceof Date && !Number.isNaN(v.getTime())) {
    // Excel-i e ndërton datën në mesnatë LOKALE; lexoje me pjesë lokale që
    // zona orare të mos e zhvendosë një ditë prapa.
    return { value: ymd(v.getFullYear(), v.getMonth() + 1, v.getDate()), ambiguous: false };
  }

  if (typeof v === 'number' && Number.isFinite(v)) {
    // Serial i Excel-it (dita 1 = 1900-01-01, me bug-un e famshëm të 1900).
    // Kufijtë: 1901-01-01 (367) deri 2199 (~109500) — jashtë tyre s'është datë.
    if (v < 367 || v > 109500) return { value: null, ambiguous: false };
    const ms = Date.UTC(1899, 11, 30) + Math.round(v) * 86400000;
    const d = new Date(ms);
    return { value: ymd(d.getUTCFullYear(), d.getUTCMonth() + 1, d.getUTCDate()), ambiguous: false };
  }

  const s = String(v).trim();
  if (!s) return { value: null, ambiguous: false };

  const iso = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (iso) return { value: safeYmd(+iso[1], +iso[2], +iso[3]), ambiguous: false };

  const sep = s.match(/^(\d{1,2})[./-](\d{1,2})[./-](\d{2,4})/);
  if (sep) {
    let [, a, b, y] = sep;
    a = +a; b = +b; y = +y;
    if (y < 100) y += y < 70 ? 2000 : 1900;
    if (a > 12 && b <= 12) return { value: safeYmd(y, b, a), ambiguous: false };   // ditë/muaj, e sigurt
    if (b > 12 && a <= 12) return { value: safeYmd(y, a, b), ambiguous: false };   // muaj/ditë, e sigurt
    return { value: safeYmd(y, b, a), ambiguous: a !== b && a <= 12 && b <= 12 };  // ditë/muaj, e pasigurt
  }

  const d = new Date(s);
  if (!Number.isNaN(d.getTime())) {
    return { value: ymd(d.getFullYear(), d.getMonth() + 1, d.getDate()), ambiguous: false };
  }
  return { value: null, ambiguous: false };
}

const TRUTHY = new Set(['1', 'true', 'yes', 'y', 'po', 'ja', 'aktiv', 'aktive', 'active', 'i punesuar', 'punesuar', 'x']);
const FALSY = new Set(['0', 'false', 'no', 'n', 'jo', 'nein', 'joaktiv', 'jo aktiv', 'inactive', 'passive', 'i larguar', 'larguar', 'terminated', 'ish punetor']);

/** Po/Jo në çfarëdo gjuhe → boolean, ose `null` kur s'kuptohet. */
export function parseBool(v) {
  if (typeof v === 'boolean') return v;
  const k = normKey(v);
  if (!k) return null;
  if (TRUTHY.has(k)) return true;
  if (FALSY.has(k)) return false;
  return null;
}

/** Numër me presje dhjetore ose me pikë, me ose pa simbol monedhe. */
export function parseNumber(v) {
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  const s = String(v ?? '').replace(/[^\d,.-]/g, '').trim();
  if (!s) return null;
  // "1.234,56" (evropiane) kundrejt "1,234.56" (angleze): fiton shenja e fundit.
  const lastComma = s.lastIndexOf(',');
  const lastDot = s.lastIndexOf('.');
  let cleaned = s;
  if (lastComma > -1 && lastDot > -1) {
    cleaned = lastComma > lastDot ? s.replace(/\./g, '').replace(',', '.') : s.replace(/,/g, '');
  } else if (lastComma > -1) {
    cleaned = s.replace(',', '.');
  }
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : null;
}

/**
 * Fjalë → çelës nga një fjalor sinonimesh.
 *
 * `{ laptop: ['laptop', 'notebook'], … }` → `matchVocabulary('Notebook', dict)`
 * kthen `'laptop'`. Kthen `null` kur nuk njihet — kurrë një parazgjedhje të
 * heshtur: ai që e lexon rreshtin duhet ta dijë se vlera e tij nuk u kuptua.
 */
export function matchVocabulary(value, dictionary) {
  const k = normKey(value);
  if (!k) return null;
  for (const [key, words] of Object.entries(dictionary || {})) {
    if (key === k) return key;
    if ((words || []).some((w) => normKey(w) === k)) return key;
  }
  return null;
}

/**
 * Lista e ndarë me `;`, `,` ose rresht të ri → varg tekstesh pa boshllëqe.
 *
 * Presja hyn si ndarëse sepse klienti e shkruan «Ngarkues, çantë, mouse» —
 * por pikëpresja mbetet e para te udhëzimet, që një emër me presje brenda
 * («Mouse, wireless») të ketë ende një rrugë për t'u shkruar i plotë.
 */
export function splitList(v) {
  const s = String(v ?? '').trim();
  if (!s) return [];
  const parts = /[;\n]/.test(s) ? s.split(/[;\n]+/) : s.split(/,/);
  return parts.map((x) => x.trim()).filter(Boolean);
}

// ── Njohja e kolonave ────────────────────────────────────────────────────────

const words = (s) => String(s || '').split(' ').filter(Boolean);

// Dy fjalë përputhen kur janë të njëjta, ose kur njëra është parashtesë e
// tjetrës dhe pjesa e përbashkët është së paku pesë shkronja. Kufiri i pesë
// shkronjave është ai që e ndan shqipen e trajtës së shquar («mbikëqyrësi» /
// «mbikëqyrësit», «departamenti» / «departamentit») nga rastësia e pastër
// («no» brenda «notes»).
const wordMatch = (a, b) =>
  a === b || (Math.min(a.length, b.length) >= 5 && (a.startsWith(b) || b.startsWith(a)));
const subsetOf = (a, b) => a.length > 0 && a.every((w) => b.some((x) => wordMatch(w, x)));

/**
 * Kokat e skedarit → `{ [columnKey]: headerIndex }` (−1 = e palidhur).
 *
 * Dy kalime, dhe rendi ka rëndësi: së pari përputhjet e SAKTA, pastaj ato të
 * pjesshme nga aliasi më i gjatë te më i shkurtri. Pa këtë, aliasi «emri» do
 * ta rrëmbente kolonën «Emri i mbikëqyrësit» para se ta shihte «Mbikëqyrësi»,
 * dhe njeriu do të gjente dy kolona të shkëmbyera pa e ditur pse.
 *
 * Kalimi i dytë krahason FJALË, jo shkronja. Nënvargu i thjeshtë dukej i
 * padëmshëm derisa fleta e klientit e provoi të kundërtën: «No» (numri i
 * rreshtit) hyri te «Notes» sepse «notes» e përmban «no», dhe «Age» hyri te
 * «Mbikëqyrësi» sepse «manager» e përmban «age». Të dyja kolona pa asnjë
 * lidhje kuptimore, të lidhura nga rastësia e shkronjave. Tani përputhja
 * kërkon që fjalët e njërës anë të jenë nëngrup i tjetrës — «Pushimi vjetor»
 * ende gjen «Pushimi vjetor (ditë)», por «No» nuk gjen asgjë.
 *
 * @param {Array<{key, label, aliases}>} columns  regjistri i kolonave të modulit
 * @param {string[]} headers                      kokat ashtu si erdhën
 */
export function detectMappingFor(columns, headers) {
  const normed = (headers || []).map(normKey);
  const taken = new Set();
  const map = {};
  columns.forEach((c) => { map[c.key] = -1; });

  const claim = (key, idx) => { map[key] = idx; taken.add(idx); };

  for (const col of columns) {
    const wanted = new Set([normKey(col.label), ...(col.aliases || []).map(normKey)]);
    const idx = normed.findIndex((h, i) => h && !taken.has(i) && wanted.has(h));
    if (idx > -1) claim(col.key, idx);
  }

  // Kalimi i dytë nuk zgjedh «më të mirën» mes disa kandidatëve — ai zgjedh
  // vetëm kur ka NJË kandidat të vetëm nga të dyja anët. Kolona «Insurance» e
  // fletës së klientit e tregoi pse: fjalën e vetme «insurance» e duan tri
  // fusha njëherësh (numri i kartelës, shuma, skadimi), dhe te ajo fletë ajo
  // kolonë mban Po/Jo — pra secila prej të treve do të ishte e gabuar, dhe
  // «Yes» do të shkruhej si numër kartele te çdo punëtor. Paqartësia i takon
  // njeriut; hapi i hartës rri aty pikërisht për këtë.
  const wantedBy = new Map();     // indeksi i kokës → Set(çelësa kolonash)
  const wantsHeaders = new Map(); // çelësi i kolonës → Set(indekse kokash)

  for (const col of columns) {
    if (map[col.key] > -1) continue;
    const aliasSets = [normKey(col.label), ...(col.aliases || []).map(normKey)]
      .filter(Boolean)
      .map(words);
    normed.forEach((h, i) => {
      // Kokat nën tri shkronja («No», «Nr», «ID») nuk hyjnë kurrë në hamendje:
      // ose përputhen saktësisht më lart, ose i zgjedh njeriu.
      if (!h || taken.has(i) || h.length < 3) return;
      const hw = words(h);
      if (!aliasSets.some((a) => subsetOf(hw, a) || subsetOf(a, hw))) return;
      if (!wantedBy.has(i)) wantedBy.set(i, new Set());
      wantedBy.get(i).add(col.key);
      if (!wantsHeaders.has(col.key)) wantsHeaders.set(col.key, new Set());
      wantsHeaders.get(col.key).add(i);
    });
  }

  for (const [headerIdx, cols] of wantedBy) {
    if (cols.size !== 1) continue;
    const key = [...cols][0];
    if (wantsHeaders.get(key).size !== 1) continue;
    if (map[key] === -1 && !taken.has(headerIdx)) claim(key, headerIdx);
  }

  return map;
}

/** Kokat e skedarit që nuk shkojnë askund — dialogu i tregon si të anashkaluara. */
export function unmappedHeaders(headers, mapping) {
  const used = new Set(Object.values(mapping || {}).filter((i) => i > -1));
  return (headers || [])
    .map((h, i) => ({ header: String(h ?? '').trim(), index: i }))
    .filter((h) => h.header && !used.has(h.index));
}

// ── Leximi i skedarit ────────────────────────────────────────────────────────

/**
 * Cili rresht është koka?
 *
 * "I pari me së paku dy qeliza të mbushura" ishte rregulli i vjetër, dhe ai
 * mjaftonte derisa shablloni i aseteve solli një rresht bande mbi kokat
 * («Identiteti i pajisjes», «Blerja dhe financat» — pesë qeliza të mbushura mbi
 * njëzet e shtatë kolona). Ai rresht u lexua si kokë, kokat e vërteta u lexuan
 * si të dhëna, dhe shablloni nuk e lexonte dot vetveten.
 *
 * Rregulli i ri numëron qelizat: mes rreshtave të parë, koka është I PARI që
 * arrin së paku 60% të mbushjes maksimale. Bandat dhe titujt bien vetvetiu
 * (një-dy qeliza), kurse një kokë me ndonjë kolonë pa emër ende fiton ndaj
 * rreshtave të të dhënave — sepse është më herët, jo sepse është më e plotë.
 */
const HEADER_SCAN = 15;
const HEADER_SHARE = 0.6;

export function findHeaderRow(grid) {
  const counts = (grid || []).slice(0, HEADER_SCAN)
    .map((r) => (r || []).filter((c) => String(c ?? '').trim()).length);
  const max = Math.max(0, ...counts);
  if (max < 2) return -1;
  const need = Math.max(2, Math.ceil(max * HEADER_SHARE));
  return counts.findIndex((n) => n >= need);
}

/**
 * ArrayBuffer (xlsx/xls/csv) → `{ headers, rows, sheetName, sheetNames }`.
 *
 * Rreshti i parë JO-bosh është koka. Fletët e eksportuara nga sisteme të tjera
 * nisin shpesh me një titull ose me një rresht bosh; po ta merrnim rreshtin 1
 * verbërisht, e gjithë harta do të dilte e zbrazët.
 *
 * @param {ArrayBuffer} data
 * @param {string} [sheetName]  fleta e zgjedhur; bosh = e para
 * @param {object} [io]         `XLSX` — injektohet nga moduli që e thërret, që
 *                              `importCells` të mbetet pa varësi ndaj bibliotekës
 */
export function readWorkbookWith(XLSX, data, sheetName) {
  const wb = XLSX.read(data, { type: 'array', cellDates: true, cellNF: false, cellText: false });
  const names = wb.SheetNames || [];
  const name = sheetName && names.includes(sheetName) ? sheetName : names[0];
  if (!name) return { headers: [], rows: [], sheetName: '', sheetNames: [] };

  const grid = XLSX.utils.sheet_to_json(wb.Sheets[name], { header: 1, blankrows: false, defval: '', raw: true });
  const headRow = findHeaderRow(grid);
  if (headRow < 0) return { headers: [], rows: [], sheetName: name, sheetNames: names };

  const headers = (grid[headRow] || []).map((h) => String(h ?? '').trim());
  const rows = grid
    .slice(headRow + 1)
    .filter((r) => (r || []).some((c) => String(c ?? '').trim() !== ''));
  return { headers, rows, sheetName: name, sheetNames: names };
}
